import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api";
import { Icon, Spinner, useToast, Modal } from "../../components/ui";

const PROC_LABEL = { SUBMISSION: "Submissions", END_PROCESS: "End Process" };
const MONTH_ABBR = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtMonth(k) { const [y, m] = k.split("-"); return `${MONTH_ABBR[+m - 1]}-${String(y).slice(2)}`; }
// build months from a start (yyyy-MM) up to the current month
function monthsFrom(start) {
  const out = []; let [y, m] = start.split("-").map(Number);
  const now = new Date(); const ey = now.getFullYear(), em = now.getMonth() + 1;
  while (y < ey || (y === ey && m <= em)) { out.push(`${y}-${String(m).padStart(2, "0")}`); m++; if (m > 12) { m = 1; y++; } }
  return out;
}

export function ClientReport() {
  const toast = useToast();
  const [groups, setGroups] = useState([]);
  const [group, setGroup] = useState("");
  const [start, setStart] = useState("2025-03");
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);   // { month, process, practopulse, client }

  useEffect(() => { setGroups(["LACN", "CCCMG"]); setGroup("LACN"); }, []);

  const load = useCallback(async () => {
    if (!group) return;
    setData(null);
    try { setData(await api.clientReport(group, monthsFrom(start).join(","))); }
    catch (e) { toast(e.message, "err"); }
  }, [group, start, toast]);
  useEffect(() => { load(); }, [load]);

  const cell = (col, proc, field) => (col[proc] ? col[proc][field] : 0);
  const rowTotal = (proc, field) => (data?.months || []).reduce((s, c) => s + (cell(c, proc, field) || 0), 0);
  const grandTotal = () => (data?.months || []).reduce((s, c) => s + (c.total || 0), 0);

  const save = async () => {
    try {
      await api.setClientReport({ group, process: edit.process, month: edit.month,
        clientCount: edit.client === "" ? 0 : Number(edit.client),
        practopulse: edit.practopulse === "" ? null : Number(edit.practopulse) });
      setEdit(null); toast("Saved", "ok"); load();
    } catch (e) { toast(e.message, "err"); }
  };

  const exportExcel = () => {
    if (!data) return;
    const esc = (v) => String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const th = (t, dark) => `<th style="background:${dark ? "#111827" : "#4f46e5"};color:#fff;padding:6px 10px;border:1px solid #ccc">${esc(t)}</th>`;
    const td = (v, opt = {}) => `<td style="padding:5px 10px;border:1px solid #ddd;text-align:${opt.left ? "left" : "center"};${opt.bold ? "font-weight:bold;" : ""}${opt.bg ? "background:" + opt.bg + ";" : ""}">${esc(v)}</td>`;
    const head = "<tr>" + th(group) + th("Total") + months.map((c) => th(fmtMonth(c.month))).join("") + "</tr>";
    let body = "<tr>" + td("Total", { left: true, bold: true, bg: "#eef1f6" }) + td(grandTotal(), { bold: true, bg: "#eef1f6" }) + months.map((c) => td(c.total || 0, { bold: true, bg: "#eef1f6" })).join("") + "</tr>";
    procs.forEach((proc) => {
      body += "<tr>" + `<td colspan="${months.length + 2}" style="background:#f1f5f9;font-weight:bold;padding:6px 10px;border:1px solid #ccc">${esc(PROC_LABEL[proc] || proc)}</td>` + "</tr>";
      body += "<tr>" + td("PractoPulse", { left: true }) + td(rowTotal(proc, "practopulse"), { bold: true }) + months.map((c) => td(cell(c, proc, "practopulse"))).join("") + "</tr>";
      body += "<tr>" + td(group, { left: true }) + td(rowTotal(proc, "client"), { bold: true }) + months.map((c) => td(cell(c, proc, "client"))).join("") + "</tr>";
    });
    body += `<tr><td colspan="${months.length + 2}" style="background:#111827;color:#fff;font-weight:bold;padding:6px 10px;border:1px solid #ccc">PHS %</td></tr>`;
    procs.forEach((proc) => {
      body += "<tr>" + td("PHS " + (PROC_LABEL[proc] === "Submissions" ? "Submission" : "End Process") + "%", { left: true }) + td("") + months.map((c) => td(cell(c, proc, "phsPct") + "%")).join("") + "</tr>";
    });
    const html = `<html><head><meta charset="utf-8"></head><body><table style="border-collapse:collapse;font-family:Arial;font-size:12px"><thead>${head}</thead><tbody>${body}</tbody></table></body></html>`;
    const blob = new Blob([html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `client-report-${group}-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  if (!group && groups.length === 0) return <div className="empty"><Spinner /></div>;

  const months = data?.months || [];
  const procs = data?.processes || ["SUBMISSION", "END_PROCESS"];

  return (
    <div className="jr-page">
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Client report</h2>
        <div className="cell-sub">Month-wise PractoPulse (our team, counted from the tool) vs the client's own team (entered manually). Click a cell to enter the client's number or override PractoPulse for past months.</div>
      </div>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Group</label>
          <select className="inp" style={{ width: 180, height: 40 }} value={group} onChange={(e) => setGroup(e.target.value)}>
            {groups.map((g) => <option key={g} value={g}>{g}</option>)}
          </select></div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: "#334155" }}>Show from month</label>
          <input className="inp" style={{ width: 170, height: 40 }} type="month" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <button className="btn btn-primary" onClick={exportExcel} disabled={!data} style={{ height: 40 }}>Export to Excel</button>
      </div>

      {!data ? <div className="empty"><Spinner /></div> : (
        <div className="card"><div className="table-wrap">
          <table className="tbl sum-tbl">
            <thead><tr>
              <th className="sum-rowhead" style={{ minWidth: 150, background: "#232c44", color: "#fff" }}>{group}</th>
              <th className="sum-num" style={{ background: "#232c44", color: "#fff" }}>Total</th>
              {months.map((c) => <th key={c.month} className="sum-num" style={{ background: "#232c44", color: "#fff" }}>{fmtMonth(c.month)}</th>)}
            </tr></thead>
            <tbody>
              <tr className="sum-total-row">
                <td className="sum-rowhead">Total</td>
                <td className="sum-num sum-total-col">{grandTotal()}</td>
                {months.map((c) => <td key={c.month} className="sum-num">{c.total || 0}</td>)}
              </tr>
              {procs.map((proc) => (
                <React.Fragment key={proc}>
                  <tr><td colSpan={months.length + 2} style={{ background: "#f1f5f9", fontWeight: 800, padding: "6px 10px" }}>{PROC_LABEL[proc] || proc}</td></tr>
                  <tr>
                    <td className="sum-rowhead" style={{ paddingLeft: 20 }}>PractoPulse</td>
                    <td className="sum-num sum-total-col">{rowTotal(proc, "practopulse")}</td>
                    {months.map((c) => <td key={c.month} className="sum-num" style={{ cursor: "pointer" }} title="Click to override (past months)" onClick={() => setEdit({ month: c.month, process: proc, practopulse: cell(c, proc, "practopulse"), client: cell(c, proc, "client") })}>{cell(c, proc, "practopulse")}</td>)}
                  </tr>
                  <tr>
                    <td className="sum-rowhead" style={{ paddingLeft: 20 }}>{group}</td>
                    <td className="sum-num sum-total-col">{rowTotal(proc, "client")}</td>
                    {months.map((c) => <td key={c.month} className="sum-num" style={{ cursor: "pointer", color: "#4f46e5" }} title="Click to enter the client's number" onClick={() => setEdit({ month: c.month, process: proc, practopulse: cell(c, proc, "practopulse"), client: cell(c, proc, "client") })}>{cell(c, proc, "client") || <span className="sum-zero">·</span>}</td>)}
                  </tr>
                </React.Fragment>
              ))}
              <tr><td colSpan={months.length + 2} style={{ height: 10, background: "#fff", border: "none" }}></td></tr>
              <tr><td colSpan={months.length + 2} style={{ background: "#111827", color: "#fff", fontWeight: 800, padding: "6px 10px" }}>PHS %</td></tr>
              {procs.map((proc) => (
                <tr key={"pct" + proc}>
                  <td className="sum-rowhead">PHS {PROC_LABEL[proc] === "Submissions" ? "Submission" : "End Process"}%</td>
                  <td className="sum-num sum-total-col">—</td>
                  {months.map((c) => <td key={c.month} className="sum-num" style={{ fontWeight: 700, color: "#047857" }}>{cell(c, proc, "phsPct")}%</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}

      {edit && (
        <Modal title={`${PROC_LABEL[edit.process] || edit.process} · ${fmtMonth(edit.month)}`} onClose={() => setEdit(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setEdit(null)}>Cancel</button><button className="btn btn-primary" onClick={save}>Save</button></>}>
          <div className="field"><label>{group} (client's own count — enter manually)</label>
            <input className="inp" type="number" min="0" value={edit.client} onChange={(e) => setEdit({ ...edit, client: e.target.value })} autoFocus /></div>
          <div className="field"><label>PractoPulse override (leave blank to auto-count from the tool)</label>
            <input className="inp" type="number" min="0" value={edit.practopulse} onChange={(e) => setEdit({ ...edit, practopulse: e.target.value })} placeholder="Auto-counted" /></div>
        </Modal>
      )}
    </div>
  );
}